import React from 'react';
import PropTypes from 'prop-types';
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import './styles.scss';
import { getLast7Days } from '../../../utils';
import { useMediaQuery } from 'react-responsive';
import useTranslation from 'hooks/useTranslation';
import { getDayInEnglish } from 'utils/mappers';

const AcceptedConversationsChart = ({
  acceptedConversations,
  closedConversations,
  closedAgentConversations,
  deniedConversations,
  isLastWeek = true,
}) => {
  const isTextRotateSize = useMediaQuery({
    query: '(max-width: 800px)',
  });
  const { getTranslation, getAppLocale } = useTranslation('WEBSITES');
  const { t } = getTranslation();
  const locale = getAppLocale();

  const ACCEPTED_CONVERSATIONS = t('ACCEPTED_CONVERSATIONS');
  const CLOSED_CONVERSATIONS = t('CLOSED_CONVERSATIONS');
  const CLOSED_AGENT_CONVERSATION = t('CLOSED_AGENT_CONVERSATION');
  const DENIED_CONVERSATION = t('DENIED_CONVERSATION');
  const margin = {
    top: 5,
    right: locale === 'fa' ? 0 : 15,
    left: locale === 'fa' ? 15 : 0,
    bottom: 5,
  };

  const data = getLast7Days().map((day, index) => {
    let adaptedDay = day;
    if (day === 'یکشنبه') {
      adaptedDay = 'یک‌شنبه';
    } else if (day === 'پنجشنبه') {
      adaptedDay = 'پنج‌شنبه';
    }
    const accepted = acceptedConversations.hasOwnProperty(adaptedDay)
      ? acceptedConversations[adaptedDay]
      : 0;

    let chartData = {
      name:
        index === 0 && isLastWeek
          ? t('TODAY')
          : getAppLocale() === 'fa'
          ? day
          : getDayInEnglish(day),
      [ACCEPTED_CONVERSATIONS]: accepted,
    };

    if (closedConversations) {
      let closed = closedConversations.hasOwnProperty(adaptedDay)
        ? closedConversations[adaptedDay]
        : 0;
      chartData[CLOSED_CONVERSATIONS] = closed;
    }
    if (closedAgentConversations) {
      let closedAgent = closedAgentConversations.hasOwnProperty(adaptedDay)
        ? closedAgentConversations[adaptedDay]
        : 0;
      chartData[CLOSED_AGENT_CONVERSATION] = closedAgent;
    }
    if (deniedConversations) {
      let denied = deniedConversations.hasOwnProperty(adaptedDay)
        ? deniedConversations[adaptedDay]
        : 0;
      chartData[DENIED_CONVERSATION] = denied;
    }

    return chartData;
  });

  const reversedData = [...data].reverse();

  return (
    <ResponsiveContainer width='99%'>
      <LineChart data={locale === 'en' ? data : reversedData} margin={margin}>
        <CartesianGrid
          stroke='#e6e6e6'
          vertical={false}
          strokeDasharray='8,3'
          strokeWidth={1}
        />
        <XAxis
          dataKey='name'
          tickLine={false}
          angle={isTextRotateSize ? -90 : 0}
          interval={0}
          dy={15}
          style={{ direction: 'ltr' }}
          dx={locale === 'fa' ? 2 : 0}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          width={45}
          orientation={locale === 'fa' ? 'right' : 'left'}
          dy={-4}
          dx={locale === 'fa' ? 8 : 0}
        />
        <Tooltip wrapperStyle={{ outline: 'none' }} />
        <Line
          type='monotone'
          dataKey={ACCEPTED_CONVERSATIONS}
          stroke='#47CD89'
          strokeWidth={2}
        />
        {closedConversations && (
          <Line
            type='monotone'
            dataKey={CLOSED_CONVERSATIONS}
            stroke='#475467'
            strokeWidth={2}
          />
        )}
        {closedAgentConversations && (
          <Line
            type='monotone'
            dataKey={CLOSED_AGENT_CONVERSATION}
            stroke='#98A2B3'
            strokeWidth={2}
          />
        )}
        {deniedConversations && (
          <Line
            type='monotone'
            dataKey={DENIED_CONVERSATION}
            stroke='#F97066'
            strokeWidth={2}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
};

AcceptedConversationsChart.propTypes = {
  acceptedConversations: PropTypes.object,
};

export default React.memo(AcceptedConversationsChart);
